/**
 * FarmOverviewMap
 *
 * Lightweight read-only Leaflet map showing a farm's irrigation devices at a glance:
 *   - Farm boundary (dashed outline)
 *   - Relay zone polygons (colored, bright if relay ON, faded if OFF)
 *   - Router markers (green/gray circle)
 *   - Sensor markers (blue circle with tooltip)
 *   - Weather station markers (orange circle with tooltip)
 *
 * No editing, no leaflet-draw, no schedule timeline.
 */

import { useEffect, useRef, useMemo } from 'react';
import {
  MapContainer,
  TileLayer,
  Polygon,
  CircleMarker,
  Tooltip,
  useMap,
} from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import type {
  OverviewController,
  OverviewSensor,
  OverviewWeatherStation,
  GeoJSONPolygon,
} from '../../services/irrigationOverview';
import { isRelayOn } from '../../services/irrigationOverview';
import { relayCountFor } from '../../services/espnowIrrigation';
import { formatNum, toNum } from '@/utils/numeric';
import { nodeState, nodeStateLabel } from '../../utils/nodeState';

// ─── GeoJSON → Leaflet coordinate conversion ─────────────────────────────────

function geoJsonRingToLatLng(ring: number[][]): [number, number][] {
  return ring.map(([lng, lat]) => [lat, lng]);
}

// ─── Props ────────────────────────────────────────────────────────────────────

interface FarmOverviewMapProps {
  boundaryGeometry: GeoJSONPolygon | null;
  centerPoint: { lat: number; lng: number } | null;
  controllers: OverviewController[];
  sensors: OverviewSensor[];
  weatherStations: OverviewWeatherStation[];
  height?: number;
  onClick?: () => void;
}

/** Inner component that uses the map instance to fit bounds */
function MapBounds({
  boundaryGeometry,
  controllers,
  sensors,
  weatherStations,
}: Pick<FarmOverviewMapProps, 'boundaryGeometry' | 'controllers' | 'sensors' | 'weatherStations'>) {
  const map = useMap();
  const fitted = useRef(false);

  const points = useMemo(() => {
    const pts: [number, number][] = [];
    if (boundaryGeometry?.coordinates?.[0]) {
      for (const [lng, lat] of boundaryGeometry.coordinates[0]) pts.push([lat, lng]);
    }
    for (const c of controllers) {
      for (const r of c.routers) {
        if (r.lat != null && r.lng != null) pts.push([r.lat, r.lng]);
      }
      for (const z of c.zones) {
        if (z.boundary_geometry?.coordinates?.[0]) {
          for (const [lng, lat] of z.boundary_geometry.coordinates[0]) pts.push([lat, lng]);
        }
      }
    }
    for (const s of sensors) {
      if (s.lat != null && s.lng != null) pts.push([s.lat, s.lng]);
    }
    for (const w of weatherStations) {
      if (w.lat != null && w.lng != null) pts.push([w.lat, w.lng]);
    }
    return pts;
  }, [boundaryGeometry, controllers, sensors, weatherStations]);

  useEffect(() => {
    if (fitted.current || points.length === 0) return;
    const bounds = L.latLngBounds(points);
    // No animation: a zoom still in flight when the page unmounts throws from Leaflet.
    map.fitBounds(bounds, { padding: [20, 20], maxZoom: 17, animate: false });
    fitted.current = true;
  }, [map, points]);

  return null;
}

export default function FarmOverviewMap({
  boundaryGeometry,
  centerPoint,
  controllers,
  sensors,
  weatherStations,
  height = 220,
  onClick,
}: FarmOverviewMapProps) {
  // Build a relay state lookup: `${router_mac}-${relay_index}` → boolean
  const relayStateMap = useMemo(() => {
    const map = new Map<string, boolean>();
    for (const c of controllers) {
      for (const r of c.routers) {
        // OverviewRouter carries no transport of its own - it comes off the
        // controller, which is what decides the fallback for a node that has
        // never reported a relay count (every XBee one).
        for (let i = 0; i < relayCountFor(r, c.transport); i++) {
          map.set(`${r.router_mac}-${i}`, isRelayOn(r.relay_states, i));
        }
      }
    }
    return map;
  }, [controllers]);

  const defaultCenter: [number, number] = centerPoint
    ? [centerPoint.lat, centerPoint.lng]
    : [-33.87, 151.21]; // fallback to Sydney

  return (
    <div
      className="farm-overview-map rounded-lg overflow-hidden border border-gray-200"
      style={{ height, cursor: onClick ? 'pointer' : undefined }}
      onClick={onClick}
    >
      <MapContainer
        center={defaultCenter}
        zoom={14}
        style={{ height: '100%', width: '100%' }}
        scrollWheelZoom={false}
        dragging={false}
        zoomControl={false}
        doubleClickZoom={false}
        touchZoom={false}
        attributionControl={false}
        preferCanvas
      >
        <TileLayer
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />

        <MapBounds
          boundaryGeometry={boundaryGeometry}
          controllers={controllers}
          sensors={sensors}
          weatherStations={weatherStations}
        />

        {/* Farm boundary — dashed outline */}
        {boundaryGeometry?.coordinates?.[0] && (
          <Polygon
            positions={geoJsonRingToLatLng(boundaryGeometry.coordinates[0])}
            pathOptions={{
              color: '#6B7280',
              weight: 2,
              dashArray: '6 4',
              fillOpacity: 0.03,
              fillColor: '#6B7280',
            }}
          />
        )}

        {/* Relay zone polygons */}
        {controllers.flatMap((c) =>
          c.zones
            .filter((z) => z.boundary_geometry?.coordinates?.[0])
            .map((z) => {
              const on = relayStateMap.get(`${z.router_mac}-${z.relay_index}`) ?? false;
              return (
                <Polygon
                  key={`${z.router_mac}-${z.relay_index}`}
                  positions={geoJsonRingToLatLng(z.boundary_geometry!.coordinates[0])}
                  pathOptions={{
                    color: z.zone_color,
                    weight: on ? 2 : 1,
                    fillColor: z.zone_color,
                    fillOpacity: on ? 0.45 : 0.15,
                  }}
                >
                  <Tooltip direction="center" permanent={false}>
                    {z.zone_name || 'Zone'} — {on ? 'ON' : 'OFF'}
                  </Tooltip>
                </Polygon>
              );
            })
        )}

        {/* Router markers */}
        {controllers.flatMap((c) =>
          c.routers
            .filter((r) => r.lat != null && r.lng != null)
            .map((r) => (
              <CircleMarker
                key={r.router_mac}
                center={[r.lat!, r.lng!]}
                radius={5}
                pathOptions={{
                  color: r.online ? '#16A34A' : '#9CA3AF',
                  fillColor: r.online ? '#22C55E' : '#D1D5DB',
                  fillOpacity: 0.9,
                  weight: 1.5,
                }}
              >
                <Tooltip>{r.name || r.router_mac} — {nodeStateLabel(nodeState(r))}</Tooltip>
              </CircleMarker>
            ))
        )}

        {/* Sensor markers */}
        {sensors
          .filter((s) => s.lat != null && s.lng != null)
          .map((s) => (
            <CircleMarker
              key={s.device_id}
              center={[s.lat!, s.lng!]}
              radius={5}
              pathOptions={{
                color: '#2563EB',
                fillColor: s.online ? '#3B82F6' : '#93C5FD',
                fillOpacity: 0.9,
                weight: 1.5,
              }}
            >
              <Tooltip>
                {s.name || s.device_id}
                {s.latest_value != null && ` — ${s.latest_value}${s.type_unit || ''}`}
              </Tooltip>
            </CircleMarker>
          ))}

        {/* Weather station markers */}
        {weatherStations
          .filter((w) => w.lat != null && w.lng != null)
          .map((w) => (
            <CircleMarker
              key={w.id}
              center={[w.lat!, w.lng!]}
              radius={6}
              pathOptions={{
                color: '#D97706',
                fillColor: '#F59E0B',
                fillOpacity: 0.9,
                weight: 1.5,
              }}
            >
              <Tooltip>
                {w.name}
                {toNum(w.latest_observation?.temp_c) != null &&
                  ` — ${formatNum(w.latest_observation?.temp_c, 1)}°C`}
              </Tooltip>
            </CircleMarker>
          ))}
      </MapContainer>
    </div>
  );
}
